import React, { ReactNode, useEffect, useRef, useState } from 'react';

import { Carousel } from '@xing-com/carousel';
import { PopUpWindow } from '@xing-com/pop-up-window';

import CustomPrescreen from '../consumers/prescreen-consumer/custom-prescreen';
import CustomPrescreenMultiPage from '../consumers/prescreen-consumer/custom-prescreen-multi-page';
import IframePrescreen from '../consumers/prescreen-consumer/iframe-prescreen';
import {
  PrescreenPayload,
  BrazeUserAttribute,
  BrazeCustomEvent,
} from '../consumers/prescreen-consumer/prescreen-consumer';
import { ALT_IMAGE_MISSING_EVENT_NAME } from '../events';
import htmlSerializer, { HtmlSerializerProps } from '../utils/html-serializer';
import { Logger } from '../utils/logger';

import { LightBoxFooter } from './light-box-footer';
import { Styled } from './light-box.styles';

interface LightBoxProps {
  data: PrescreenPayload;
  onClose: () => void;
  onPrescreenClick: () => void;
  onPrescreenButtonClick: () => void;
  onContentShown: () => void;
  setBrazeCustomAttribute: (payload: BrazeUserAttribute) => void;
  onURLButtonClick: (
    url: string,
    target?: '_self' | '_blank',
    buttonId?: string
  ) => void;
  sendBrazeCustomEvent: (payload: BrazeCustomEvent) => void;
  sendBrazeAltImageMissingEvent: (payload: BrazeCustomEvent) => void;
}

const ValidatedPopupImage = ({
  imageUrl,
  altImage,
  fallbackAlt,
  onClick,
  sendBrazeAltImageMissingEvent,
}: {
  imageUrl: string;
  altImage?: string;
  fallbackAlt?: string;
  onClick: () => void;
  sendBrazeAltImageMissingEvent: (payload: BrazeCustomEvent) => void;
}): ReactNode | null => {
  const eventSent = useRef(false);

  useEffect(() => {
    if (!altImage && !eventSent.current) {
      sendBrazeAltImageMissingEvent({ name: ALT_IMAGE_MISSING_EVENT_NAME });
      eventSent.current = true;
    }
  }, [altImage, sendBrazeAltImageMissingEvent]);

  if (!altImage) return null;

  return (
    <Styled.PopupWindowImage
      src={imageUrl}
      alt={altImage || fallbackAlt}
      title={altImage || fallbackAlt}
      onClick={onClick}
    />
  );
};

const isValidUrl = (url: string): boolean => {
  try {
    // eslint-disable-next-line node/no-unsupported-features/node-builtins
    const urlObj = new URL(url);
    return urlObj.protocol === 'https:';
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (err) {
    return false;
  }
};

const LightBox = ({
  data,
  onClose,
  onPrescreenClick,
  onPrescreenButtonClick,
  onContentShown,
  sendBrazeCustomEvent,
  sendBrazeAltImageMissingEvent,
  onURLButtonClick,
}: LightBoxProps): ReactNode | null => {
  const [popUpWindowVisible, setPopUpWindowVisible] = useState(true);
  const popupRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (popUpWindowVisible) {
      onContentShown();
    } else {
      onClose();
    }
  }, [popUpWindowVisible]);

  const popUpWindowHide = (): void => setPopUpWindowVisible(false);
  let popUpWindowContent;
  let noPadding = true;
  let ariaLabel = data.title || '';

  const popUpWindowActions =
    !data.isHTML && data.hasButton ? (
      <LightBoxFooter
        button1AriaLabel={data.button1AriaLabel}
        button2AriaLabel={data.button2AriaLabel}
        buttonColor={data.buttonColor}
        button1Color={data.button1Color}
        button2Color={data.button2Color}
        buttons={data.buttons}
        onClick={onPrescreenButtonClick}
        onClose={onClose}
      ></LightBoxFooter>
    ) : null;

  if (data.isHTML && data.message) {
    noPadding = false;
    const sanitizedMessage = data.message.replace(/(\r\n|\n|\r)/gm, '');
    const jsonString = sanitizedMessage.match(
      /<PrescreenJson>(.*)<\/PrescreenJson>/
    )?.[1];

    if (jsonString === undefined) return null;

    try {
      const schema = JSON.parse(jsonString);
      switch (schema.type) {
        case 'carousel':
          ariaLabel = schema.title || undefined;
          popUpWindowContent = (
            <Carousel
              {...(schema.title ? { 'aria-label': schema.title } : {})}
              hideScrollbar
              itemWidthConfined="100%"
              itemSnapPosition="center"
              tabbable={true}
            >
              {schema.value.map(
                (card: HtmlSerializerProps['content'][], cardIndex: number) => (
                  <div key={`card-${cardIndex}`}>
                    {card.map((content, contentIndex) => (
                      <React.Fragment
                        key={`card-${cardIndex}-content-${contentIndex}`}
                      >
                        {htmlSerializer({
                          content,
                          onClose,
                          onURLButtonClick,
                          sendBrazeAltImageMissingEvent,
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                )
              )}
            </Carousel>
          );
          break;
        case 'iframe':
          // don't allow invalid url
          if (!isValidUrl(schema.url)) {
            Logger.log('Braze provider - invalid url');
            return null;
          }
          noPadding = true;
          ariaLabel = schema.title || undefined;
          popUpWindowContent = (
            <IframePrescreen
              url={schema.url}
              title={schema.iframeTitle}
              introScreen={schema.introScreen}
              onClose={onClose}
              introScreenButtonText={schema.introScreenButtonText}
              introScreenButtonAriaLabel={schema.introScreenButtonAriaLabel}
            />
          );
          break;
        case 'custom':
          popUpWindowContent = (
            <CustomPrescreen
              content={schema.value}
              onClose={onClose}
              onURLButtonClick={onURLButtonClick}
              sendBrazeCustomEvent={sendBrazeCustomEvent}
              sendBrazeAltImageMissingEvent={sendBrazeAltImageMissingEvent}
            />
          );
          break;
        case 'custom-multi-page':
          ariaLabel = schema.title || undefined;
          popUpWindowContent = (
            <div ref={popupRef}>
              <CustomPrescreenMultiPage
                content={schema.value}
                popupRef={popupRef}
                fallback={schema.fallback}
                onClose={onClose}
                onURLButtonClick={onURLButtonClick}
                sendBrazeCustomEvent={sendBrazeCustomEvent}
                sendBrazeAltImageMissingEvent={sendBrazeAltImageMissingEvent}
              />
            </div>
          );
          break;
      }
    } catch (e) {
      Logger.log(e);
      return null;
    }
  } else if (data.template === 'full_background') {
    popUpWindowContent = (
      <Styled.FullSizeDiv
        backgroundImageURL={data.template ? data.imageUrl : undefined}
        mobileBackgroundImageURL={data.mobileImageURL}
        onClick={onPrescreenClick}
      >
        <Styled.FullBackgroundFooter>
          {popUpWindowActions}
        </Styled.FullBackgroundFooter>
      </Styled.FullSizeDiv>
    );

    return (
      <PopUpWindow
        data-qa="lightbox-pop-up-window"
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        isShown={popUpWindowVisible}
        hide={data.removeCrossIcon ? undefined : popUpWindowHide}
        noPadding={noPadding}
        onDimmerClick={popUpWindowHide}
      >
        {popUpWindowContent}
      </PopUpWindow>
    );
  } else if (data.messageClickAction === 'URI' && data.messageUrl) {
    popUpWindowContent = (
      <ValidatedPopupImage
        imageUrl={data.imageUrl}
        altImage={data.altImage}
        fallbackAlt={data.buttonText || data.messageUrl}
        onClick={onPrescreenClick}
        sendBrazeAltImageMissingEvent={sendBrazeAltImageMissingEvent}
      />
    );
  } else {
    popUpWindowContent = (
      <ValidatedPopupImage
        imageUrl={data.imageUrl}
        altImage={data.altImage}
        fallbackAlt={data.buttonText || data.messageUrl}
        onClick={onPrescreenClick}
        sendBrazeAltImageMissingEvent={sendBrazeAltImageMissingEvent}
      />
    );
  }

  return (
    <PopUpWindow
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      data-qa="lightbox-pop-up-window"
      isShown={popUpWindowVisible}
      hide={data.removeCrossIcon ? undefined : popUpWindowHide}
      noPadding={noPadding}
      onDimmerClick={popUpWindowHide}
    >
      {popUpWindowContent}
      <div>{popUpWindowActions}</div>
    </PopUpWindow>
  );
};

export { LightBox };
